import { makeStyles } from 'tss-react/mui';

import { BadgeSize, BadgeStatus } from './Badge';

interface BadgeStyleProps {
  status: BadgeStatus;
  size: BadgeSize;
  border: string;
}

const useStyles = makeStyles<BadgeStyleProps>()(
  (theme, { status, size, border }) => {
    const statusColors = {
      [BadgeStatus.DEFAULT]: {
        backgroundColor: theme.new.color.grey[100],
        borderColor: theme.new.color.grey[400],
        color: theme.new.color.text.secondary,
      },
      [BadgeStatus.SUCCESS]: {
        backgroundColor: theme.new.color.success[100],
        borderColor: theme.new.color.success[600],
        color: theme.new.color.success[600],
      },
      [BadgeStatus.DANGER]: {
        backgroundColor: theme.new.color.danger[100],
        borderColor: theme.new.color.danger[600],
        color: theme.new.color.danger[600],
      },
      [BadgeStatus.INFO]: {
        backgroundColor: theme.new.color.brand[200],
        borderColor: theme.new.color.brand[600],
        color: theme.new.color.brand[600],
      },
      [BadgeStatus.WARNING]: {
        backgroundColor: theme.new.color.warning[100],
        borderColor: theme.new.color.warning[700],
        color: theme.new.color.warning[1000],
      },
      [BadgeStatus.TEAL]: {
        backgroundColor: theme.new.color.teal[100],
        borderColor: theme.new.color.teal[700],
        color: theme.new.color.teal[700],
      },
      [BadgeStatus.PURPLE_GRADIENT]: {
        background: `linear-gradient(
    102deg,
    ${theme.new.color.purple[200]} 0%,
    ${theme.new.color.brand[200]} 100%
  )`,
        borderColor: theme.new.color.brand[600],
        color: theme.new.color.purple[600],
      },
      [BadgeStatus.WHITE]: {
        backgroundColor: theme.new.color.common.white,
        borderColor: theme.new.color.grey[400],
        color: theme.new.color.grey[600],
      },
    };

    const sizeStyles = {
      [BadgeSize.SMALL]: {
        height: '20px',
        padding: `2px ${theme.new.spacing.small}`,
      },
      [BadgeSize.LARGE]: {
        height: '32px',
        padding: `${theme.new.spacing.xsmall} ${theme.new.spacing.small}`,
      },
    };

    const borderStyles = {
      border: border,
    };

    return {
      chip: {
        ...statusColors[status],
        ...sizeStyles[size],
        ...borderStyles,
        // The color-less `border` shorthand above resets border-color to
        // currentColor, which would drag the WARNING border to the dark label
        // color. Re-declare the shorthand with the gold token so the border and
        // text can differ, matching Figma (SDPCPLT-7492).
        ...(status === BadgeStatus.WARNING && {
          border: `1px solid ${statusColors[status].borderColor}`,
        }),
        borderRadius: theme.new.spacing.xsmall,
        '& .MuiChip-label': {
          padding: 0,
          ...(size === BadgeSize.SMALL
            ? theme.typography.caption_medium
            : theme.typography.body_medium),
        },
        '& .MuiChip-icon': {
          marginRight: theme.new.spacing.xsmall,
        },
      },
    };
  }
);

export default useStyles;
